import { useCallback, useEffect, useState } from 'react';
import { ChromecastCustomData } from '../../interfaces';
import { useCastContext } from '../cast-context';
import { getMedia } from '../utils/media';
import { useCastMedia } from './use-media-session';
import { useRemotePlayerEvent } from './use-remote-player-event';

interface CastMetadata {
  title?: string;
  sectionDuration?: number;
  sectionStartTimeInMedia?: number;
  sectionStartAbsoluteTime?: number;
}

interface BaseMediaInfo extends Omit<chrome.cast.media.MediaInfo, 'customData' | 'metadata'> {
  title: string;
  customData: ChromecastCustomData;
  metadata?: CastMetadata;
}

interface LiveMediaInfo extends BaseMediaInfo {
  startAbsoluteTime: number | undefined;
  streamType: chrome.cast.media.StreamType.LIVE;
}

interface OtherMediaInfo extends BaseMediaInfo {
  streamType: chrome.cast.media.StreamType.OTHER;
}

interface BufferedMediaInfo extends BaseMediaInfo {
  streamType: chrome.cast.media.StreamType.BUFFERED;
}

export type MediaInfo = LiveMediaInfo | OtherMediaInfo | BufferedMediaInfo;

export type CastInfo = MediaInfo;

export function useCastInfo(session: cast.framework.CastSession | undefined) {
  const { player } = useCastContext();
  const { media } = useCastMedia(session);
  const [info, setInfo] = useState<CastInfo | undefined>(() => player?.mediaInfo as CastInfo);

  useEffect(() => {
    setInfo(player?.mediaInfo as CastInfo);
  }, [player?.mediaInfo]);

  useEffect(() => {
    if (media) {
      setInfo(media.media as CastInfo);
    }
  }, [media]);

  const handleUpdate = useCallback(
    (isAlive: boolean) => {
      const mediaSession = getMedia(session);
      if (isAlive && mediaSession) {
        setInfo(mediaSession.media as CastInfo);
      }
    },
    [session],
  );

  useEffect(() => {
    if (media) {
      media.addUpdateListener(handleUpdate);

      return () => {
        media.removeUpdateListener(handleUpdate);
      };
    }
  }, [handleUpdate, media]);

  useRemotePlayerEvent('mediaInfoChanged', (event) => {
    console.log(event);
    if (event.value) {
      setInfo(event.value);
    }
  });

  return info;
}
